import React, { useEffect, useState } from 'react';
import { Link, Outlet, useParams, useNavigate } from 'react-router-dom';
import { HomeIcon, ClipboardDocumentListIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import { COMMUNITY_LINKS } from '../../utils/constants';

// Normalise a Ghana number to the 233XXXXXXXXX form wa.me expects.
const toWhatsAppNumber = (raw?: string): string | null => {
  if (!raw) return null;
  let n = String(raw).replace(/[^\d]/g, '');
  if (n.startsWith('0')) n = '233' + n.slice(1);
  if (!/^233\d{9}$/.test(n)) return null;
  return n;
};

const ShopLayout: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [agentWhatsApp, setAgentWhatsApp] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadAgentContact = async () => {
      if (!username) {
        setAgentWhatsApp(null);
        return;
      }
      try {
        const resp = await api.get(`/storefront/public/${encodeURIComponent(username)}`);
        const contact = resp.data?.data?.contact;
        const number = toWhatsAppNumber(contact?.whatsapp) || toWhatsAppNumber(contact?.phone);
        if (!cancelled) setAgentWhatsApp(number);
      } catch {
        if (!cancelled) setAgentWhatsApp(null);
      }
    };
    loadAgentContact();
    return () => { cancelled = true; };
  }, [username]);

  const contactHref = agentWhatsApp
    ? `https://wa.me/${agentWhatsApp}?text=${encodeURIComponent('Hello, I need help with an order from your store.')}`
    : COMMUNITY_LINKS.WHATSAPP_DIRECT;

  return (
    <div className={`min-h-screen bg-[#f8faf9] dark:bg-navy-900 ${theme}`}>
      {/* Minimal top nav */}
      <nav className="sticky top-0 z-40 bg-white dark:bg-navy-800 border-b border-gray-100 dark:border-navy-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          {/* Logo + store name */}
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={isAuthenticated ? '/dashboard' : '/'}
              className="flex items-center gap-2.5 group shrink-0"
            >
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-sm">
                <img src="/logo-dark.svg" alt="" className="w-5 h-5" draggable={false} />
              </div>
            </Link>
            {username && (
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-gray-300 dark:text-gray-600">/</span>
                <span className="text-sm font-semibold text-navy-900 dark:text-white truncate">
                  @{username}
                </span>
              </div>
            )}
          </div>

          {/* Right actions */}
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors shrink-0"
            >
              Dashboard
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/login"
                className="text-sm font-medium text-navy-700 dark:text-navy-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors px-3 py-1.5"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-white px-4 py-1.5 rounded-lg transition-colors"
              >
                Register
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Page content — add bottom padding on mobile for the nav */}
      <div className="pb-16 lg:pb-0">
        <Outlet />
      </div>

      {/* Mobile-only bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-navy-800 border-t border-gray-100 dark:border-navy-700 safe-area-bottom">
        <div className="flex items-center justify-around h-14">
          <button
            onClick={() => navigate(username ? `/${username}` : '/packages')}
            className="flex flex-col items-center gap-0.5 px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <HomeIcon className="h-5 w-5" />
            <span className="text-[10px] font-medium">Home</span>
          </button>
          <button
            onClick={() => navigate('/orders')}
            className="flex flex-col items-center gap-0.5 px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ClipboardDocumentListIcon className="h-5 w-5" />
            <span className="text-[10px] font-medium">Orders</span>
          </button>
          <a
            href={contactHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-0.5 px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            <span className="text-[10px] font-medium">Contact</span>
          </a>
        </div>
      </nav>
    </div>
  );
};

export default ShopLayout;
